<template>
    <div>
        <div class="subtitle has-text-weight-semibold is-size-4">{{ formsHeader }}</div>
        <div class="divider is-success">Formulare</div>
        <div class="columns column-control is-vcentered is-centered">
            <div class="column is-12">
                <b-field class="is-fullwidth">
                    <b-input
                        :loading="isLoading"
                        @keypress.native.enter="onSearch"
                        placeholder="Cauta dupa Nume/Prenume/Telefon/Judet/Localitate"
                        type="search"
                        v-model.trim="search"
                        icon="magnify"
                        class="is-fullwidth"
                        expanded
                    >
                    </b-input>
                    <p class="control">
                        <b-button @click="onSearch" type="is-primary" label="Cauta" />
                    </p>
                </b-field>
            </div>
        </div>
        <b-table
            class="has-text-left"
            :loading="isLoading"
            :checkable="false"
            :paginated="true"
            :total="formsCount"
            :per-page="formsLimit"
            :striped="true"
            :hoverable="true"
            :data="formsList"
            backend-sorting
            backend-pagination
            @page-change="onPageChange"
            @sort="onSort"
            aria-next-label="Next page"
            aria-previous-label="Previous page"
            aria-page-label="Page"
            aria-current-label="Current page"
            :default-sort-direction="defaultSortOrder"
            :default-sort="[defaultSortField, defaultSortOrder]"
            :debounce-search="1000"
        >
            <b-table-column sortable label="Nume" field="nume" v-slot="props">
                {{ props.row.nume }}
            </b-table-column>
            <b-table-column sortable label="Prenume" field="prenume" v-slot="props">
                {{ props.row.prenume }}
            </b-table-column>
            <b-table-column sortable label="Telefon" field="telefon" v-slot="props">
                {{ props.row.telefon }}
            </b-table-column>
            <b-table-column sortable label="Judet" field="judet" v-slot="props">
                {{ props.row.forms_address.judet }}
            </b-table-column>
            <b-table-column sortable label="Localitate" field="localitate" v-slot="props">
                {{ props.row.forms_address.localitate }}
            </b-table-column>
            <!-- <b-table-column sortable centered label="Manual" field="manual" v-slot="props">
                <b-tag :type="+props.row.manual == 1 ? 'is-warning' : 'is-info'">{{
                    +props.row.manual == 1 ? 'Da' : 'Nu'
                }}</b-tag>
            </b-table-column> -->
            <b-table-column sortable centered label="Perioada" field="perioada" v-slot="props">
                {{ props.row.perioada > 1 ? `${props.row.perioada} ani` : '1 an' }}
            </b-table-column>
            <b-table-column centered :label="`Dublat ${pastYear}`" field="dublat" v-slot="props">
                <b-tag :type="+props.row.dublat == 1 ? 'is-warning' : 'is-info'">{{
                    +props.row.dublat == 1 ? 'Da' : 'Nu'
                }}</b-tag>
            </b-table-column>
            <b-table-column field="forms_rejected.reason" centered label="Depus" sortable v-slot="props">
                <template v-if="props.row.forms_export != null && props.row.forms_rejected == null">
                    <b-tooltip
                        :label="`Formular depus si acceptat de ANAF in data de ${moment(
                            props.row.forms_export.createdAt
                        ).format('DD/MM/YYYY')}`"
                        type="is-dark"
                        position="is-top"
                    >
                        <b-icon
                            icon="circle"
                            size="is-small"
                            :class="{ bullet: true, rejected: true, active: props.row.forms_export.id != null }"
                        >
                        </b-icon>
                    </b-tooltip>
                </template>
                <template v-else-if="props.row.forms_rejected != null">
                    <b-tooltip
                        :label="`Formular respins : ${props.row.forms_rejected.reason}`"
                        type="is-dark"
                        position="is-top"
                    >
                        <b-icon
                            icon="circle"
                            size="is-small"
                            :class="{
                                bullet: true,
                                'is-clickable': true,
                                rejected: true,
                                active: props.row.forms_rejected == null,
                            }"
                        >
                        </b-icon>
                    </b-tooltip>
                </template>
                <template v-else>
                    <b-icon icon="circle" size="is-small" :class="{ bullet: true, rejected: false, pending: true }">
                    </b-icon>
                </template>
            </b-table-column>
            <b-table-column :visible="onlyManualForms" centered sortable label="Aprobat" field="status" v-slot="props">
                <b-tag :type="+props.row.status == 1 ? 'is-success' : 'is-danger'">{{
                    +props.row.status == 1 ? 'Da' : 'Nu'
                }}</b-tag>
            </b-table-column>
            <b-table-column sortable centered label="Data" field="createdAt" v-slot="props">
                {{ props.row.createdAt == null ? 'Necunoscuta' : moment(props.row.createdAt).format('DD/MM/YYYY') }}
            </b-table-column>
            <section slot="empty" class="section">
                <div class="content has-text-grey has-text-centered">
                    <template v-if="isLoading">
                        <p>
                            <b-icon icon="dots-horizontal" size="is-large" />
                        </p>
                        <p>Se incarca...</p>
                    </template>
                    <template v-else>
                        <p>
                            <b-icon icon="emoticon-sad" size="is-large" />
                        </p>
                        <p>Nu exista formulare</p>
                    </template>
                </div>
            </section>
        </b-table>
    </div>
</template>

<script>
import * as toast from '@/utils/toast';
import { mapGetters, mapState } from 'vuex';
import moment from 'moment';
import debounce from 'lodash/debounce';

export default {
    data: () => ({
        isLoading: false,
        moment,
    }),
    computed: {
        ...mapState({
            defaultSortField: (state) => state.family.forms.sortField,
            defaultSortOrder: (state) => state.family.forms.sortOrder,
            manualForms: (state) => state.family.forms.manual,
        }),
        ...mapGetters({
            currentPage: 'family/currentPage',
            formsList: 'family/formsList',
            formsCount: 'family/formsCount',
            formsLimit: 'family/formsLimit',
            formsSearch: 'family/formsSearch',
            formsPageYear: 'family/formsPageYear',
            onlyManualForms: 'family/onlyManualForms',
        }),
        formsHeader() {
            return `Formulare ${this.$route.params.source} ${this.$route.params.year}: ${this.formsCount}`;
        },
        pastYear() {
            const year = Number(this.$route.params?.year);
            if (Number.isFinite(year)) return year - 1;
            return 'anul trecut';
        },
        search: {
            get() {
                return this.formsSearch;
            },
            set(value) {
                this.$store.commit('family/SET_FORMS_PAGINATION_FIELD', { field: 'search', value });
            },
        },
    },
    methods: {
        onPageChange(page) {
            this.$store.commit('family/SET_FORMS_PAGINATION_FIELD', { field: 'page', value: page });
            return this.getForms();
        },
        async onSort(field, order) {
            this.$store.commit('family/SET_FORMS_PAGINATION_FIELD', { field: 'sortField', value: field });
            this.$store.commit('family/SET_FORMS_PAGINATION_FIELD', { field: 'sortOrder', value: order });
            return this.getForms();
        },
        async getForms() {
            this.isLoading = true;
            try {
                await this.$store.dispatch('family/GetForms');
            } catch (error) {
                console.log(error);
                this.$reportToSentry(err, {
                    extra: {
                        fn: 'familygetForms',
                        params: {
                            event: 'familygetForms',
                            err,
                        },
                    },
                });
                return toast.error('Eroare la preluarea formularelor');
            } finally {
                this.isLoading = false;
            }
        },
        onSearch() {},
    },
    watch: {
        '$route.params.source'(val) {
            this.$store.commit('family/SET_FORMS_PAGINATION_FIELD', {
                field: 'manual',
                value: val === 'online' ? 0 : 1,
            });
            this.$forceUpdate();
            return this.getForms();
        },
        '$route.params.year'(val) {
            this.$store.commit('family/SET_FORMS_YEAR', val);
            this.$forceUpdate();
            return this.getForms();
        },
    },
    async mounted() {
        if (
            this.$route.params?.source == null ||
            (this.$route.params?.source !== 'online' && this.$route.params?.source !== 'hartie')
        ) {
            return this.$router.push({ name: 'FamilyFormular', params: { source: 'online', year: 2025 } });
        }
        const manual = this.$route.params?.source === 'online' ? 0 : 1;
        const source = this.$route.params?.source;
        if (this.$route.params?.year == null) {
            return this.$router.push({ name: 'FamilyFormular', params: { source: source, year: 2025 } });
        }
        this.$store.commit('family/SET_FORMS_YEAR', this.$route.params?.year);
        this.$store.commit('family/SET_FORMS_PAGINATION_FIELD', { field: 'manual', value: manual });
        this.onSearch = debounce(() => {
            return this.getForms();
        }, 300);
        return this.getForms();
    },
};
</script>

<style></style>
